import React, { useState } from 'react';
import {
  alpha,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Stack,
} from '@mui/material';
import { Add, Close, Delete, Done, Edit, Save } from '@mui/icons-material';
import { TagType } from '../../../../config/tag';
import { ChromeColorPickerUI, FormLabelUI, InputUI } from '../../../Interface';

const defaultTagData = { name: '', tagType: TagType.LABEL, color: '#ffffff' };

const renderEditFields = (tagData, setTagData, setColorAnchorEl, resetEdit, handleSave) => {
  return (
    <Box sx={{ p: 1.5 }} onKeyDown={(e) => e.stopPropagation()}>
      <Stack spacing={1}>
        <InputUI
          fullWidth
          label={<FormLabelUI text="Címke neve" />}
          variant="outlined"
          size="small"
          value={tagData.name}
          onChange={(e) =>
            setTagData((prev) => ({
              ...prev,
              name: e.target.value,
            }))
          }
        />

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <FormLabelUI text="Szín:" />
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              bgcolor: tagData.color || '#ffffff',
              boxShadow: '0px 1px 8px rgba(0,0,0,0.3)',
              cursor: 'pointer',
              flexShrink: 0,
            }}
            onClick={(e) => setColorAnchorEl(e.currentTarget)}
          />
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ mt: '0 !important' }}>
          <IconButton size="small" onClick={resetEdit}>
            <Close fontSize="small" />
          </IconButton>

          <IconButton size="small" color="primary" onClick={handleSave} disabled={!tagData.name.trim()}>
            <Save fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );
};

const WorkspaceTagMenu = ({ labels = [], selectedTags = [], onTagChange, onTagCreate, onTagUpdate, onTagDelete }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [colorAnchorEl, setColorAnchorEl] = useState(null);
  const [editingTagId, setEditingTagId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [tagData, setTagData] = useState(defaultTagData);

  const resetEdit = () => {
    setEditingTagId(null);
    setIsAdding(false);
    setTagData(defaultTagData);
    setColorAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    resetEdit();
  };

  const handleAdd = () => {
    resetEdit();
    setIsAdding(true);
  };

  const handleEdit = (tag) => {
    resetEdit();
    setEditingTagId(tag.id);
    setTagData({
      name: tag.name,
      color: tag.color || defaultTagData.color,
    });
  };

  const handleSave = async () => {
    if (!tagData.name.trim()) return;

    const data = {
      ...tagData,
      name: tagData.name.trim(),
    };

    if (isAdding) {
      await onTagCreate(data);
    } else {
      await onTagUpdate(editingTagId, data);
    }

    resetEdit();
  };

  return (
    <>
      <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
        <Edit fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {labels.map((tag) => {
          const checked = selectedTags?.some((link) => link.tagId === tag.id);

          if (editingTagId === tag.id) {
            return (
              <Box key={tag.id}>
                <Divider />
                {renderEditFields(tagData, setTagData, setColorAnchorEl, resetEdit, handleSave)}
                <Divider />
              </Box>
            );
          }

          return (
            <MenuItem key={tag.id} onClick={() => onTagChange(tag.id)}>
              <ListItemIcon>{checked && <Done fontSize="small" />}</ListItemIcon>

              <ListItemText>
                <Chip
                  label={tag.name}
                  sx={{
                    bgcolor: alpha(tag.color, 0.2),
                    color: tag.color,
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: alpha(tag.color, 0.3),
                    },
                  }}
                />
              </ListItemText>
              <Stack direction="row" spacing={0.5} sx={{ ml: 2 }}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(tag);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>

                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagDelete(tag.id);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            </MenuItem>
          );
        })}

        <Divider />

        {isAdding ? (
          renderEditFields(tagData, setTagData, setColorAnchorEl, resetEdit, handleSave)
        ) : (
          <MenuItem onClick={handleAdd}>
            <ListItemIcon>
              <Add fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Új címke" />
          </MenuItem>
        )}
      </Menu>
      <Popover
        open={Boolean(colorAnchorEl)}
        anchorEl={colorAnchorEl}
        onClose={() => setColorAnchorEl(null)}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
      >
        <ChromeColorPickerUI
          color={tagData.color}
          onChange={(color) => {
            setTagData((prev) => ({
              ...prev,
              color: color.hex,
            }));
          }}
        />
      </Popover>
    </>
  );
};

export default WorkspaceTagMenu;
